'use client';

import { Button, SheetClose } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { ChipValue } from '../Chip/Chip';
import { FilterButton } from '../filters/components/FilterButton/FilterButton';
import { FilterWithChips } from '../filters/components/FilterWithChips/FilterWithChips';
import { SortingSection } from '../filters/components/SortingSection/SortingSection';
import { VolatilityFilter } from '../filters/components/VolatilityFilter/VolatilityFilter';
import { staticGameFilters } from '../filters/staticFilters';
import { useGameFilters } from './hooks/useGameFilters';

type VolatilityFilterValue = 'Low' | 'Mid' | 'High' | 'Extreme';
type VolatilityType = {
  min: VolatilityFilterValue;
  max: VolatilityFilterValue;
};
type GamesFiltersType = {
  sorting?: string;
  volatility?: VolatilityType;
  gameProviders?: string[];
  minimumBet?: string[];
};

function GamesFilter({ gameProviders = [] }: { gameProviders?: ChipValue<string>[] }) {
  const t = useTranslations('casino.filters');
  const { gameFilters, filtersCount, updateGameFilters } = useGameFilters();
  const [filters, setFilters] = useState<GamesFiltersType>(gameFilters);

  const handleFilterChange = (filterName: keyof GamesFiltersType, value: GamesFiltersType[keyof GamesFiltersType]) => {
    const currentFilters = filters;
    setFilters({ ...currentFilters, [filterName]: value });
  };

  const handleClearAll = () => {
    setFilters({});
    updateGameFilters({});
  };

  const handleFilter = () => {
    setFilters(filters);
    setTimeout(() => {
      updateGameFilters(filters);
    }, 300);
  };

  const onOpen = () => {
    setFilters(gameFilters);
  };

  return (
    <FilterButton appliedFiltersCount={filtersCount} title={t('title')} onOpen={onOpen}>
      <div className="pb-6 px-6 flex flex-col gap-8 mb-auto" data-testid="filters-container">
        <SortingSection
          options={staticGameFilters.sortingOptions}
          value={filters.sorting || 'popularity'}
          onChange={value => handleFilterChange('sorting', value)}
          sectionTitle="Sorting"
        />
        <VolatilityFilter
          namespace="casino.filters.volatility"
          onValueChange={value => handleFilterChange('volatility', value as VolatilityType)}
          value={[filters.volatility?.min, filters.volatility?.max]}
        />
        <FilterWithChips
          namespace="casino.filters.game_provider"
          onValueChange={value => handleFilterChange('gameProviders', value)}
          chips={gameProviders}
          value={filters.gameProviders || []}
          hasTooltip
          data-track-container="Providers"
          data-testid="providers-filter"
        />
      </div>
      <div
        className="sticky bottom-0 right-0 left-0 w-full flex justify-between items-center gap-6 p-5 shadow-top-m bg-container-base rounded-t-lg"
        data-track-container="Actions"
        data-testid="actions-container"
      >
        <Button
          className="basis-1/2"
          variant="secondary"
          size="m"
          disabled={Object.keys(filters).length === 0}
          onClick={handleClearAll}
          data-track-name="Clear"
          data-testid="clear-button"
        >
          {t('clear_all')}
        </Button>
        <SheetClose asChild>
          <Button className="basis-1/2" variant="primary" size="m" onClick={handleFilter} data-track-name="Show" data-testid="show-button">
            {t('show_games')}
          </Button>
        </SheetClose>
      </div>
    </FilterButton>
  );
}

export { GamesFilter, type GamesFiltersType, type VolatilityFilterValue };
